import MultiPageBuilder from 'components/MultiPageBuilder';
import Heading from 'components/Heading';
import Spacing from 'components/Spacing';
import ButtonGroup from 'components/ButtonGroup';
import Button from '@mui/material/Button';
import { Typography } from '@mui/material';
import GridContainer from 'components/GridContainer';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { realisation as rules } from 'further-ui/validations';
import ConsiderationEntry from './ConsiderationEntry';
import { ConsiderationTypeLabels } from 'further-ui/labels';
import api from 'lib/trpcClient';
import { CurrencyCode, Vehicle } from 'further-types/lp/vehicle';
import { styled } from '@mui/material';
import { numberToCurrencyString } from 'further-ui/utils';
import { RealisationFormState } from '../types';
import { useFormWithStateContext } from 'contexts/FormWithStateContext';
import { useNotification } from 'hooks/ui/useNotification';
import { groupBy, round, sumBy } from 'lodash';

type Props = {
  onPrevious: () => void;
  onNext: () => void;
  vehicle: Vehicle;
};

const ScrollableGridContainer = styled(GridContainer)({
  overflow: 'auto',
});

const EnterConsideration: React.FC<Props> = ({
  onPrevious,
  onNext,
  vehicle,
}) => {
  const { control, watch, getValues } =
    useFormContext<rules.CreateRealisationFormValues>();
  const notification = useNotification();

  const { fields } = useFieldArray({
    control,
    name: 'consideration',
  });

  const { state } = useFormWithStateContext<
    rules.CreateRealisationFormValues,
    RealisationFormState
  >();

  const assetId = getValues('assetId');

  const { data: asset } = api.asset.get.useQuery(
    {
      vehicleId: vehicle._id,
      assetId,
    },
    {
      enabled: !!vehicle._id && !!assetId,
    },
  );

  const validateAllocationPercentages = (
    considerations?: Array<{
      investorAllocations?: Array<{
        allocationPercentage: number;
        investmentId: string;
        holdingClassId: string;
      }>;
    }>,
  ) => {
    if (!considerations || considerations.length === 0) return true;

    return considerations.every((c) => {
      const allocations = c.investorAllocations ?? [];
      if (allocations.length === 0) return true;

      const groups = groupBy(
        allocations,
        (a) => `${a.holdingClassId}-${a.investmentId}`,
      );

      return Object.values(groups).every((group) => {
        const total = round(sumBy(group, 'allocationPercentage'), 2);
        return total === 100;
      });
    });
  };

  const considerations = watch('consideration');

  const handleNext = () => {
    if (validateAllocationPercentages(considerations)) {
      onNext();
    } else {
      notification.error(
        'Please ensure that the allocation percentages sum to 100%.',
      );
    }
  };

  const isNextStepEnabled = state.consideration.every(
    (consideration) => consideration.showAllocationsTable,
  );

  const holdingClassRealisation = watch('holdingClassRealisation');

  const totalCashConsiderationValue = () => {
    const cashConsiderations = holdingClassRealisation.reduce((acc, curr) => {
      return (
        acc +
        (curr.cashConsiderationAmounts?.receivedConsiderationTotal ?? 0) +
        (curr.cashConsiderationAmounts?.escrowConsiderationTotal ?? 0) +
        (curr.cashConsiderationAmounts?.earnoutConsiderationTotal ?? 0) +
        (curr.cashConsiderationAmounts?.withholdingTaxPayable ?? 0)
      );
    }, 0);

    return cashConsiderations;
  };

  const defaultConsiderationCurrency =
    considerations[0]?.currency ?? asset?.baseCurrency ?? CurrencyCode.GBP;

  const getConsiderationTitle = (index: number) => {
    return `Enter consideration #${index + 1}`;
  };

  const getConsiderationCurrency = (index: number) => {
    return considerations[index].currency;
  };

  return (
    <ScrollableGridContainer>
      <Spacing size="md">
        <MultiPageBuilder
          summaryHeader="Realisation summary"
          summaryFooterTitle="Total realisation value"
          summaryFooterTotals={
            <Typography fontWeight="bold">
              {numberToCurrencyString(totalCashConsiderationValue(), {
                currency: defaultConsiderationCurrency,
              })}
            </Typography>
          }
          defaultActivePageIndex={0}
        >
          {considerations.map((_, index) => (
            <MultiPageBuilder.Page
              key={fields[index].id}
              title={`${getConsiderationTitle(index)}: ${
                ConsiderationTypeLabels[considerations[index].considerationType]
              }`}
              totals={`${numberToCurrencyString(totalCashConsiderationValue(), {
                currency: defaultConsiderationCurrency,
              })} total value`}
            >
              <Heading variant="h2">{getConsiderationTitle(index)}</Heading>
              <ConsiderationEntry
                index={index}
                currency={getConsiderationCurrency(index)}
                selectedVehicle={vehicle}
              />
            </MultiPageBuilder.Page>
          ))}
        </MultiPageBuilder>
        <ButtonGroup>
          <Button
            variant="outlined"
            onClick={onPrevious}
            sx={{ alignSelf: 'flex-start' }}
          >
            Previous step
          </Button>
          <Button variant="contained">Add consideration type</Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!isNextStepEnabled}
          >
            Go to summary
          </Button>
        </ButtonGroup>
      </Spacing>
    </ScrollableGridContainer>
  );
};

export default EnterConsideration;
